import { CheckboxButtonGroupValue } from "../../models/Button";
import { CardFlag, CardType, getDefaultCardCostSelectorState, getDefaultCardTypeSelectorState, getDefaultRaritySelectorState, getDefaultTextSymbolSelectorState } from "../../models/Card";
import { TextTag } from "../../models/CardAbility";
import { CardElementName, getDefaultElementSelectorState } from "../../models/CardElement";
import { CardRarity, CardSerialType, CARD_RARITIES } from "../../models/CardRarity";
import { CARD_TEXT_RULE_NAMES, CardTextRuleType, VariantRuleSet } from "../../models/CardRuleEngine";
import { CardVariant, CardVariantName, getDefaultCardVariantOptions } from "../../models/CardVariant";
import { ComboBoxOption } from "../../models/Input";
import { MATH_COMPARISON_SYMBOLS, MathComparisonSymbol, MathOperator } from "../../models/Math";
import { PriceDictionary } from "../../models/Price";
import { CARD_TEXT_RULES } from "../CardRulesEngine";
import { SortCommands } from "../sort/SortCommands";
import { SortType, getSortByOptions } from "../sort/SortCards";
import { FilterState } from "./FilterComponent";
import { CategoryFilter } from "./card/CategoryFilter";
import { CollectionFilter } from "./card/CollectionFilter";
import { CostFilter } from "./card/CostFilter";
import { ElementFilter } from "./card/ElementFilter";
import { FullArtFilter } from "./card/FullArtFilter";
import { JobFilter } from "./card/JobFilter";
import { PlayableFilter } from "./card/PlayableFilter";
import { PowerFilter } from "./card/PowerFilter";
import { PrimaryVariantFilter } from "./card/PrimaryVariantFilter";
import { PromoFilter } from "./card/PromoFilter";
import { SearchTargetRuleFilter } from "./card/SearchTargetRuleFilter";
import { SpecialFilter } from "./card/SpecialFilter";
import { TextFilter } from "./card/TextFilter";
import { TypeFilter } from "./card/TypeFilter";
import { VariantTypeFilter } from "./card/VariantTypeFilter";
import { SearchTextMatchMethod } from "../../models/SearchTextMatchMethod";
import { UserSettings } from "../../models/UserSettings";
import { RarityFilter } from "./card/RarityFilter";

export const CardFilterCommands = {
    primaryVariant: new PrimaryVariantFilter(),
    playable: new PlayableFilter(),
    promo: new PromoFilter(),
    fullArt: new FullArtFilter(),
    element: new ElementFilter(),
    power: new PowerFilter(),
    searchTarget: new SearchTargetRuleFilter(),
    type: new TypeFilter(),
    cost: new CostFilter(),
    special: new SpecialFilter(),
    variantType: new VariantTypeFilter(),
    collection: new CollectionFilter(),
    category: new CategoryFilter(),
    job: new JobFilter(),
    rarity: new RarityFilter(),
    text: new TextFilter()
};

export function filterCards<T extends CardVariant>(
    items: T[],
    filterState: CardFilterState,
    filterConfig?: CardFilterConfig
) {
    const matchingItems: Record<string, T> = {};

    const specialFilter = filterState.flagState.find(x => x.data === "special");
    const exBurstFilter = filterState.flagState.find(x => x.data === "exburst");
    const genericFilter = filterState.flagState.find(x => x.data === "generic");
    const limitBreakFilter = filterState.flagState.find(x => x.data === "limitbreak");
    const rarityFilters: CardRarity[] = filterState.serialTypeState
        .filter(x => x.isChecked && CARD_RARITIES.includes(x.data! as CardRarity))
        .map(x => x.data! as CardRarity);

    const promoFilter = filterState.serialTypeState.find(x => x.data === "PR");
    const elements = filterState.elementState.filter(x => x.isChecked).map(x => x.data!);
    const cardTypes = filterState.cardTypeState.filter(x => x.isChecked).map(x => x.data!);
    const costs = filterState.costState.filter(x => x.isChecked).map(x => x.data!);
    const variantTypes = filterState.variantTypeState.filter(x => x.isChecked).map(x => x.data!);
    const categories = (filterState.categoryState || []).map(x => x.value);
    const jobs = (filterState.jobState || []).map(x => x.value);

    const textTokens = CardFilterCommands.text.tokenize(filterState.searchText || "");
    const nameTokens = CardFilterCommands.text.tokenize(filterState.nameText || "");
    const effectTokens = CardFilterCommands.text.tokenize(filterState.effectText || "");

    for (const item of items) {
        let isMatch = true;

        isMatch = isMatch && CardFilterCommands.searchTarget.isMatch(item, { ruleSet: filterState.searchTargetRule?.rule });
        if (!isMatch)
            continue;

        isMatch = isMatch && CardFilterCommands.type.isMatch(item, { types: cardTypes });
        if (!isMatch)
            continue;

        isMatch = isMatch && CardFilterCommands.cost.isMatch(item, { costs: costs });
        if (!isMatch)
            continue;

        isMatch = isMatch && CardFilterCommands.element.isMatch(item, { elements: elements, isAnd: filterState.elementIsAnd });;
        if (!isMatch)
            continue;

        if (specialFilter?.isChecked) {
            isMatch = isMatch && CardFilterCommands.special.isMatch(item);
            if (!isMatch) {
                continue;
            }
        }

        if (exBurstFilter?.isChecked) {
            isMatch = isMatch && !!item.card.isExBurst;
        }

        if (!isMatch) {
            continue;
        }

        if (genericFilter?.isChecked) {
            isMatch = isMatch && !!item.card.isMultiPlayable;
        }

        if (!isMatch) {
            continue;
        }

        if (limitBreakFilter?.isChecked) {
            isMatch = isMatch && !!item.card.isLB;
        }

        if (!isMatch) {
            continue;
        }

        isMatch = isMatch && CardFilterCommands.rarity.isMatch(item, { rarities: rarityFilters });;

        if (!isMatch) {
            continue;
        }

        if (promoFilter?.isChecked) {
            isMatch = isMatch && CardFilterCommands.promo.isMatch(item);
        }

        if (!isMatch) {
            continue;
        }

        if (filterState?.opusState?.length) {
            const opus = item.opus;
            const altOpus = item.altOpus;

            isMatch = isMatch &&
                (
                    filterState.primarySerialOnly
                        ? altOpus
                            ? filterState.opusState.some(x => x.value === altOpus)
                            : filterState.opusState.some(x => x.value === opus)
                        : filterState.opusState.some(x => x.value === opus || x.value === altOpus)
                );
        }

        if (filterState?.jobState) {
            isMatch = isMatch && CardFilterCommands.job.isMatch(item, { jobs: jobs, isAnd: filterState.jobIsAnd });
            if (!isMatch)
                continue;
        }

        if (filterState?.categoryState) {
            isMatch = isMatch && CardFilterCommands.category.isMatch(item, { categories: categories, isAnd: filterState.categoryIsAnd });
            if (!isMatch)
                continue;
        }

        if (filterState?.artistState) {
            isMatch = isMatch && (item.artists ?? []).includes(filterState.artistState.value);
        }

        isMatch = isMatch && CardFilterCommands.power.isMatch(item, { operator: filterState.powerComparisonSymbolState.value, power: filterState.powerState });
        if (!isMatch)
            continue;

        isMatch = isMatch && CardFilterCommands.collection.isMatch(item, {
            collection: filterConfig?.collection,
            operator: filterState.collectedComparisonSymbolState.value,
            target: filterState.collectedState
        });

        if (!isMatch)
            continue;

        isMatch = isMatch && CardFilterCommands.variantType.isMatch(item, { types: variantTypes });
        if (!isMatch)
            continue;

        let effectTextMatch = true;
        for (const selection of filterState.effectTextState) {
            const rule = CARD_TEXT_RULES[selection.value];
            const match = rule(item);
            effectTextMatch = effectTextMatch && match;
        }

        isMatch = isMatch && effectTextMatch;
        if (!isMatch) {
            continue;
        }

        if (filterState.abilityTextSymbolState.some(x => x.isChecked)) {
            isMatch = isMatch && filterState.abilityTextSymbolState.filter(x => x.isChecked).some((buttonState) => item.card?.text?.includes(buttonState.data ?? ""));
        }

        isMatch = isMatch && CardFilterCommands.text.isMatch(item, { text: textTokens, effect: effectTokens, name: nameTokens, matchMethod: filterState.searchTextMatchMethod });

        if (!isMatch)
            continue;

        matchingItems[item.id] = item;
    }

    const result: T[] = Object.values(matchingItems);
    result.sort((a, b) => {
        return (a?.card?.number ?? 0) - (b?.card?.number ?? 0);
    });

    return SortCommands.cards.sort(result, { sortType: filterState.sortByState.value, prices: filterConfig?.prices });
}


export const getDefaultCardFilterState = (config: {
    userSettings: UserSettings,
    primarySerialOnly: boolean,
    defaultSortOption?: SortType,
    defaultShowVariants?: boolean,
    searchTargetRule?: VariantRuleSet,
    priceSorting?: boolean
}) => {
    const sortOptions = getSortByOptions();

    if (!config.priceSorting) {
        const priceAscIndex = sortOptions.findIndex(x => x.value === "Price (Asc)");
        if (priceAscIndex !== -1) {
            sortOptions.splice(priceAscIndex, 1);
        }

        const priceDescIndex = sortOptions.findIndex(x => x.value === "Price (Desc)");
        if (priceDescIndex !== -1) {
            sortOptions.splice(priceDescIndex, 1);
        }
    };

    const defaultSortType: SortType = config.defaultSortOption ?? "Opus (Desc) / Number (Asc)";
    const defaultSort = sortOptions.find(x => x.value === defaultSortType)!;

    const defaultCardFilterState: CardFilterState = {
        effectText: "",
        nameText: "",
        searchTextMatchMethod: config.userSettings.defaultSearchTextMatchMethod ?? "all",
        searchTargetRule: config.searchTargetRule ?? null,
        primarySerialOnly: config.primarySerialOnly,
        searchText: null,
        cardTypeState: getDefaultCardTypeSelectorState(),
        serialTypeState: getDefaultRaritySelectorState(),
        elementState: getDefaultElementSelectorState(),
        elementIsAnd: false,
        flagState: [
            {
                label: "Special",
                data: "special",
                isChecked: false
            },
            {
                label: "EX Burst",
                data: "exburst",
                isChecked: false
            },
            {
                label: "Generic",
                data: "generic",
                isChecked: false
            },
            {
                label: "LB",
                data: "limitbreak",
                isChecked: false
            }
        ],
        abilityTextSymbolState: getDefaultTextSymbolSelectorState(),
        sortByOptions: sortOptions,
        sortByState: defaultSort,
        jobState: [],
        jobIsAnd: false,
        categoryState: [],
        categoryIsAnd: false,
        artistState: null,
        opusState: [],
        powerState: null,
        powerComparisonSymbolState: MATH_COMPARISON_SYMBOLS[0],
        costState: getDefaultCardCostSelectorState(),
        effectTextState: [],
        effectTextOptions: Object.values(CARD_TEXT_RULE_NAMES).map((item) => ({ label: item.name, value: item.key })),
        costComparisonSymbolState: MATH_COMPARISON_SYMBOLS[0],
        collectedState: null,
        collectedComparisonSymbolState: MATH_COMPARISON_SYMBOLS[0],
        variantTypeState: getDefaultCardVariantOptions()
    }

    return defaultCardFilterState;
}

export type CardDisplayState<TItem extends CardVariant> = {
    itemsDisplayed: number,
    filters: CardFilterState,
    items: TItem[],
    isFilterModalOpen: boolean
}

export type CardFilterConfig = {
    collection?: Record<string, number>,
    prices?: PriceDictionary
}

export type CardFilterState = FilterState & {
    nameText: string | null,
    effectText: string | null,
    searchTextMatchMethod: SearchTextMatchMethod,
    searchTargetRule: VariantRuleSet | null,
    primarySerialOnly: boolean,
    cardTypeState: CheckboxButtonGroupValue<CardType>[],
    serialTypeState: CheckboxButtonGroupValue<CardSerialType>[],
    elementState: CheckboxButtonGroupValue<CardElementName>[],
    elementIsAnd: boolean,
    flagState: CheckboxButtonGroupValue<CardFlag>[],
    abilityTextSymbolState: CheckboxButtonGroupValue<TextTag>[],
    effectTextState: ComboBoxOption<CardTextRuleType>[],
    effectTextOptions: ComboBoxOption<CardTextRuleType>[],
    sortByOptions: ComboBoxOption<SortType>[],
    sortByState: ComboBoxOption<SortType>,
    jobState: ComboBoxOption<string>[] | null,
    jobIsAnd: boolean,
    categoryState: ComboBoxOption<string>[] | null,
    categoryIsAnd: boolean,
    artistState: ComboBoxOption<string> | null,
    powerState: number | null,
    powerComparisonSymbolState: ComboBoxOption<MathComparisonSymbol>,
    costState: CheckboxButtonGroupValue<number>[],
    costComparisonSymbolState: ComboBoxOption<MathComparisonSymbol>,
    opusState: ComboBoxOption<number>[] | null,
    collectedState: number | null,
    collectedComparisonSymbolState: ComboBoxOption<MathComparisonSymbol>,
    variantTypeState: CheckboxButtonGroupValue<CardVariantName>[]
}